import type { I18nMessageObject } from '@_/types';
import type { UseCommentMutationOptions } from './useCommentMutation.ts';
import clsx from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';
import { buttonCommonI18n } from '@_/i18n-common';
import { Avatar, Button, Text } from '@_/ui';
import { getI18nValue } from '@_/utils';
import { i18nMessages } from '../../shared/i18nMessages.ts';
import { CommentBodyWrapper } from '../CommentBody/index.ts';
import { CommentContent, Editable, EditorProvider, useCreateEditor } from '../Editor/index.ts';
import { useCommentMutation } from './useCommentMutation.ts';

interface CommentEditorProps<TComment> extends Pick<
  UseCommentMutationOptions<TComment>,
  'createComment' | 'onStatusChange' | 'onSuccess'
> {
  author: {
    name: I18nMessageObject | string;
    avatarUrl: string | null;
  };
  autoFocus?: boolean;
  onCancel?: () => void;
  parentCommentId?: string;
  postId: string;
  showCancelButton?: boolean;
}

export function CommentEditor<TComment>({
  author,
  autoFocus,
  createComment,
  onCancel,
  onStatusChange,
  onSuccess,
  parentCommentId,
  postId,
  showCancelButton
}: CommentEditorProps<TComment>) {
  const intl = useIntl();

  const mutation = useCommentMutation({
    createComment,
    onSuccess,
    onStatusChange
  });

  const { editor, isValid, value } = useCreateEditor({
    contentConstructor: CommentContent,
    createDefaultContent: CommentContent.createDefaultContent,
    disabled: mutation.isPending,
    extensions: CommentContent.extensions,
    placeholder: intl.formatMessage(i18nMessages.writeComment),
    validator: CommentContent.validate
  });

  const authorName =
    typeof author?.name === 'string' ? author?.name : getI18nValue(author?.name, intl.locale, true);

  return (
    <div
      className={clsx(
        'overflow-hidden rounded-md border border-gray-300 shadow-xs',
        'focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500'
      )}
    >
      <div className="px-3 pt-3 pb-1">
        <div className="flex items-center gap-x-2">
          <Avatar className="size-6" name={author?.name} src={author?.avatarUrl} />
          <Text size="xs" subdued>
            {parentCommentId ? (
              <FormattedMessage
                id="component:label.reply-as-user"
                defaultMessage="Reply as <b>{name}</b>"
                values={{
                  b: msg => (
                    <Text size="xs" semibold>
                      {msg}
                    </Text>
                  ),
                  name: authorName
                }}
              />
            ) : (
              <FormattedMessage
                {...i18nMessages.commentAsUser}
                values={{
                  b: msg => (
                    <Text size="xs" semibold>
                      {msg}
                    </Text>
                  ),
                  name: authorName
                }}
              />
            )}
          </Text>
        </div>
      </div>
      <div
        className="h-16 cursor-text overflow-y-auto bg-white px-4 py-3"
        onClick={() => editor?.commands.focus()}
      >
        <CommentBodyWrapper>
          {!!editor && (
            <EditorProvider editor={editor}>
              <Editable autoFocus={autoFocus} />
            </EditorProvider>
          )}
        </CommentBodyWrapper>
      </div>
      <div className="-mr-1 flex items-center justify-end gap-x-2 border-t border-gray-200 px-4 py-2">
        {showCancelButton && (
          <Button disabled={mutation.isPending} onClick={onCancel} size="xs" variant="plain">
            <FormattedMessage {...buttonCommonI18n.cancel} />
          </Button>
        )}
        <Button
          disabled={!isValid}
          color="primary"
          loading={mutation.isPending}
          onClick={() =>
            mutation.mutateAsync({
              contentJson: value,
              postId,
              parentCommentId
            })
          }
          size="xs"
        >
          <FormattedMessage id="component.button.post-comment" defaultMessage="Comment" />
        </Button>
      </div>
    </div>
  );
}
